import React, {FC} from 'react';
import styled from 'styled-components';

import {
  accountsCustomFieldsLearnMoreLink,
  accountsPrepareCsvLearnMoreLink,
} from '../../../../../../../../../src/helpers/accountHelpers';
import {openInBrowser} from '../../../../../../../../../srcCommon/browser/openInBrowser';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {useUserTheme} from '../../../../../../../../shared/ui/src/theme/userThemeProvider';
import {InfoBox} from '../../../common/infobox/infoBox';
import {AccountsCsvImportFileAttachment} from './accountsCsvImportFileAttachment';

/*
 * Styles.
 */

const gap = 16;
const StyledInfoBoxWrapperDiv = styled.div`
  padding: 15px 53px 24px 0px;
  background: ${(p) => p.theme.foreground};

  display: flex;
  flex-direction: column;
  gap: ${gap}px;
`;

const StyledParagraph = styled.p`
  margin-top: 5px;
  margin-bottom: 0;
  &:nth-of-type(1) {
    margin-top: 0;
  }
`;

const StyledUnorderedList = styled.ul`
  padding: 0;
  margin-top: 0;
  margin-bottom: 0;
  margin-right: 0;
  margin-left: -${gap / 2}px; // used to center the bullet point under i icon
`;

const StyledListItem = styled.li`
  margin-top: 8px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const StyledSpan = styled.span`
  margin-left: ${gap / 2}px; // matches value in StyledUnorderedList
  display: block;
`;

const StyledFileViewWrapperDiv = styled.div`
  margin-top: 12px;
  margin-bottom: 3px;
`;

/*
 * Component.
 */

export const AccountsCsvImportInfoBoxes: FC = () => {
  const theme = useUserTheme();
  const onPrepareLearnMoreClick = () => openInBrowser(accountsPrepareCsvLearnMoreLink);
  const onCustomFieldsLearnMoreClick = () => openInBrowser(accountsCustomFieldsLearnMoreLink);

  return (
    <StyledInfoBoxWrapperDiv>
      <InfoBox
        backgroundColor={theme.palette.grey.shade10}
        title={<Trans id="accounts-csv-import-infobox-prepare-csv-title">Preparing your CSV</Trans>}
        footer={<Trans id="accounts-csv-import-infobox-prepare-csv-learn-more-link">Learn more</Trans>}
        onFooterClick={onPrepareLearnMoreClick}
      >
        <StyledUnorderedList>
          <StyledListItem>
            <StyledSpan>
              <Trans id="accounts-csv-import-infobox-prepare-csv-body-item-1">
                Supported headings: name (required), description, domains, externalId, and any custom fields.
              </Trans>
            </StyledSpan>
          </StyledListItem>
          <StyledListItem>
            <StyledSpan>
              <Trans id="accounts-csv-import-infobox-prepare-csv-body-item-2">Maximum 3000 rows.</Trans>
            </StyledSpan>
          </StyledListItem>
        </StyledUnorderedList>
        <StyledFileViewWrapperDiv>
          <AccountsCsvImportFileAttachment
            name="Template.csv"
            url="https://drive.google.com/uc?id=1sXK-m9SVpixCmruBCo5_K-HM1qqsGtJg&export=download"
            size={109}
            contentType="text/csv"
          />
        </StyledFileViewWrapperDiv>
      </InfoBox>
      <InfoBox
        iconName="help"
        backgroundColor={theme.palette.grey.shade10}
        title={<Trans id="accounts-csv-import-infobox-custom-fields-title">What are custom fields?</Trans>}
        footer={<Trans id="accounts-csv-import-infobox-custom-fields-learn-more-link">Learn more</Trans>}
        onFooterClick={onCustomFieldsLearnMoreClick}
      >
        <StyledParagraph>
          <Trans id="accounts-csv-import-infobox-custom-fields-body-1">
            Front allows you to add more details to your accounts; such as Revenue, Account Manager, or
            Account Tier.
          </Trans>
        </StyledParagraph>
        <StyledParagraph>
          <Trans id="accounts-csv-import-infobox-custom-fields-body-2">
            These account custom fields will then be shown to users in Contact Details and available within
            rules to automate actions.
          </Trans>
        </StyledParagraph>
      </InfoBox>
    </StyledInfoBoxWrapperDiv>
  );
};
